import { SiteUrls } from '@tableau/shared-urls';
import { SagaIterator } from 'redux-saga';
import { call, takeEvery } from 'redux-saga/effects';
import { appStore } from '../../app/AppStore';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { Location } from '../../common/react/Location';
import { Projects } from '../../ts/resources/Projects';
import { UploadWorkbookErrorCodes, WorkbookUploadResponse, WorkbookView } from '../../ts/restApi/WorkbookUpload';
import { ReactModalService } from '../../ts/services/ReactModalService';
import { getErrorSourceFromRestError, RestErrorSource } from '../../ts/services/RestApiService';
import { TelemetryService } from '../../ts/services/TelemetryService';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import {
  removeUploadByIdAction,
  requestUploadInProgressAction,
  requestUploadWorkbookAction,
  UploadWorkbookActionsTypes,
  UploadWorkbookStartAction } from '../actions/UploadWorkbookActions';
import { UploadProgressDialogOpts } from '../components/ConnectedUploadProgressDialog';
import { UploadWorkbookDialogOpts } from '../components/ConnectedUploadWorkbookDialog';
import { ConfirmOverwriteDialogOpts } from '../components/OverwriteConfirmDialog';
import UploadWorkbookRequestHelper from '../helpers/UploadWorkbookRequestHelper';
import { sendUploadWorkbookActionTelemetry } from '../helpers/UploadWorkbookTelemetryEvents';

export async function uploadWorkbook(action: UploadWorkbookStartAction) {
  const toaster = ServiceLocator.get<ToasterService>('ToasterService');

  const id = `${action.data.projectId}-${action.data.name}`;
  const progressDispatcher = generateUpdateUploadProgressHandler(id);
  const cancelToken = UploadWorkbookRequestHelper.createCancelToken();
  const cancelHandler = () => { cancelToken.cancel('user cancelled'); };

  const projectLuid = await getProjectLuid(action.data.projectId);
  const fileSize = action.data.file.size;
  const fileName = action.data.file.name;

  const progressDialog = ReactModalService.open(UploadProgressDialogOpts(
    id, fileSize, fileName, cancelHandler));

  let isPublishSuccessful: boolean;
  let errorMsg: string;
  let uploadStartTime: any;
  let uploadEndTime: any;

  try {
    uploadStartTime = Date.now();
    const result = await UploadWorkbookRequestHelper.uploadWorkbookRequest(action, projectLuid, progressDispatcher, cancelToken);
    uploadEndTime = Date.now();
    toaster.pop({
      type: ToastType.Success,
      text: Messages.workbookUploadSuccessMessage(),
      keepOnLocationChange: true,
    });
    appStore.dispatch(removeUploadByIdAction({ id }));
    navigateToViewFromResult(result);
    isPublishSuccessful = true;
  } catch (error) {
    progressDialog.close();
    appStore.dispatch(removeUploadByIdAction({ id }));
    if (getErrorSourceFromRestError(error) !== RestErrorSource.Cancelled) {
      switch(error.data?.error?.code) {
        case String(UploadWorkbookErrorCodes.PUBLISHING_OVERWRITE):
            showConfirmOverwriteDialog();
            break;
        default:
            showErrorToast(error);
            break;
      }
      isPublishSuccessful = false;
    }
  }

  function showErrorToast(error: any) {
    const toastId = toaster.pop({
      type: ToastType.Error,
      text: UploadWorkbookRequestHelper.getErrorMessage(error),
      actionMessage: UploadWorkbookRequestHelper.getActionMessage(error),
      action: () => {
        ReactModalService.open(UploadWorkbookDialogOpts(action.data));
        toaster.close(toastId);
      }
    });
  }

  function showConfirmOverwriteDialog() {
    ReactModalService.open(ConfirmOverwriteDialogOpts({
      workbookName: action.data.name,
      submitWorkbookUpload: () => {
        const overwrite = true;
        appStore.dispatch(requestUploadWorkbookAction({ data: action.data, overwrite: overwrite }));
      },
      openUploadWorkbookDialog: () => {
        ReactModalService.open(UploadWorkbookDialogOpts(action.data));
      }
    }));
  }
  const uploadDuration = uploadEndTime - uploadStartTime;
  sendUploadWorkbookActionTelemetry(uploadDuration, fileSize, isPublishSuccessful, errorMsg);
}

function generateUpdateUploadProgressHandler(id: string) {
  return (progress: ProgressEvent<XMLHttpRequestUpload>) => {
    const action = requestUploadInProgressAction({
      id,
      bytesUploaded: progress.loaded,
      totalBytes: progress.total
    });
    // The progressHandler is not run in the saga context, so we need to use appStore instead of put
    appStore.dispatch(action);
  };
}

function navigateToViewFromResult(result: WorkbookUploadResponse) {
  const siteUrls = new SiteUrls({
    siteUrlName: SessionSiteSelectors.siteUrlName(appStore.getState()),
    queryString: window.location.search,
  });

  const defaultView = result.workbook.views.view.find((view: WorkbookView) => view.id === result.workbook.defaultViewId);
  const viewUrl = siteUrls.viewUrl(`${result.workbook.contentUrl}/${defaultView.viewUrlName}`);
  Location.instance.setUrl(`${viewUrl}?:workbookUpload=web`);
}

async function getProjectLuid(projectId: string) {
  const projectInfo = await ServiceLocator.get<Projects>('Projects').getById(projectId).then(projectInfo => projectInfo);
  return projectInfo.luid;
}

/**
 * The main saga for upload content
 */
export function* rootUploadContentSaga(): SagaIterator {
  yield takeEvery(UploadWorkbookActionsTypes.START, uploadWorkbook);
}
